import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IBasicEntityBase } from '../../data/Common'
import { IProcHeader } from '../../data/Procedure'
import { MonitorState } from './monitorStore'
import { getProcDataAsync } from './sliceMonitorData'
import { ISetFieldPayload } from '../run/sliceTimeline'
import { calculateStateParameters } from '../run/sliceRunProcHeader'

export interface IProcHeaderState {
    data: IProcHeader | null
    allowUpdates: boolean
    status: 'idle' | 'loading' | 'failed'
    error: string | null
}

const initialState: IProcHeaderState = {
    data: null,
    allowUpdates: true,
    status: 'idle',
    error: null,
}

export const getProcHeaderAsync = createAsyncThunk<
    IProcHeader,
    IBasicEntityBase,
    { rejectValue: string }
>(
    'monitorProcHeader/fetchProcHeader',
    async (identity, { dispatch, rejectWithValue }) => {
        try {
            const requestUrl = `/api/getProcHeader?partitionKey=${identity.partitionKey}&rowKey=${identity.rowKey}`
            const response = await fetch(requestUrl)

            if (!response.ok) {
                const errorMessage = `Error fetching proc header ${identity.partitionKey}:${identity.rowKey}. Status: ${response.status}`
                console.log(errorMessage)
                return rejectWithValue(errorMessage)
            }

            const json: IProcHeader = await response.json()
            dispatch(getProcDataAsync(identity))
            return json
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Unknown error while fetching procedure header'

            console.log(
                `Error fetching proc header ${identity.partitionKey}:${identity.rowKey}. ${errorMessage}`
            )

            return rejectWithValue(errorMessage)
        }
    }
)

export const procHeaderSlice = createSlice({
    name: 'monitorProcHeader',
    initialState,
    reducers: {
        setAllowUpdates: (state, action: PayloadAction<boolean>) => {
            state.allowUpdates = action.payload
        },

        setHeader: (state, action: PayloadAction<IProcHeader>) => {
            state.data = action.payload
            calculateStateParameters(action.payload)
        },

        setfield: (state, action: PayloadAction<ISetFieldPayload>) => {
            void state
            void action
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProcHeaderAsync.pending, (state) => {
                state.status = 'loading'
                state.error = null
            })
            .addCase(getProcHeaderAsync.fulfilled, (state, action) => {
                state.status = 'idle'
                state.data = action.payload
                state.error = null
            })
            .addCase(getProcHeaderAsync.rejected, (state, action) => {
                state.status = 'failed'
                state.data = null
                state.error = action.payload ?? action.error.message ?? 'Failed to fetch procedure header'
            })
    },
})

export const selectProcHeader = (state: MonitorState) => state.procHeaderSlice.data
export const selectStatus = (state: MonitorState) => state.procHeaderSlice.status
export const selectAllowUpdates = (state: MonitorState) => state.procHeaderSlice.allowUpdates
export const selectProcHeaderError = (state: MonitorState) => state.procHeaderSlice.error

export const { setAllowUpdates, setfield, setHeader } = procHeaderSlice.actions

export default procHeaderSlice.reducer